import React, { useState } from 'react';
import HomeIcon from '@material-ui/icons/Home';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import { ViewList } from '@material-ui/icons';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarSpace,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import InfoIcon from '@material-ui/icons/Info';
import SportsScoreIcon from '@mui/icons-material/SportsScore';
import useAsync from 'react-use/lib/useAsync';
import { configApiRef, identityApiRef, useApi } from '@backstage/frontend-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';
import { includesAdminGroup, PermissionConfig } from '@eng-portal/czi-extensions-common';
import KeyIcon from '@mui/icons-material/Key';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AssessmentIcon from '@material-ui/icons/Assessment';
import AssignmentIcon from '@material-ui/icons/Assignment';
import { AssumedUserMenuItem } from '../../components/Root/AssumedUser';
import { SidebarLogo } from '../../components/Root/Logo';
import { ArgusIcon } from '@eng-portal/plugin-argus';
import { OrionIcon } from '@eng-portal/plugin-orion';
import NotificationsIcon from '@material-ui/icons/Notifications';
import { NavContentBlueprint } from '@backstage/plugin-app-react';
import type { ExtensionDefinition } from '@backstage/frontend-plugin-api';

export const SidebarContent: ExtensionDefinition = NavContentBlueprint.make({
  params: {
    component: () => {
      const catalogApi = useApi(catalogApiRef);
      const config = useApi(configApiRef);
      const identityApi = useApi(identityApiRef);
      const agentRegistryEnabled = config.getOptionalString('plugins.agentRegistry.enabled') === 'true';

      const [isAdminUser, setIsAdminUser] = useState(false);
      const [templates, setTemplates] = useState([] as Entity[]);
      const [currentUserGroups, setCurrentUserGroups] = useState<string[]>([]);

      useAsync(async (): Promise<void> => {
        const templateResp = await catalogApi.getEntities({
          filter: { kind: 'Template' },
        });
        setTemplates(templateResp.items);

        const userGroups = (await identityApi.getBackstageIdentity()).ownershipEntityRefs;
        setCurrentUserGroups(userGroups);
        setIsAdminUser(includesAdminGroup(userGroups, { config }));
      }, []);

      return (
        <Sidebar sidebarOptions={{ drawerWidthOpen: 235 }}>
          <SidebarLogo />
          <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
            <SidebarSearchModal />
          </SidebarGroup>
          <SidebarDivider />
          <SidebarGroup label="Menu" icon={<MenuIcon />}>
            <SidebarItem icon={HomeIcon} to="/" text="Home" />
            { templates.length > 0 && <SidebarItem icon={CreateComponentIcon} to="create" text="Automations" /> }
            <SidebarItem icon={ViewList} to="catalog" text="Catalog" />
            <SidebarItem icon={NotificationsIcon} to="notifications" text="Notifications" />
          </SidebarGroup>
          <SidebarDivider />

            { isAdminUser
                && <SidebarItem icon={ArgusIcon} to="argus" text="Argus" /> }

            <SidebarItem icon={AssignmentIcon} to="infra-requests" text="Infra Requests" />

            <SidebarItem icon={AssessmentIcon} to="reports" text="Catalog Reports" />

            { agentRegistryEnabled
                && <SidebarItem icon={SmartToyIcon} to="agent-registry" text="Agent Registry" /> }

            { PermissionConfig.keyManagementRootView.canAccess(currentUserGroups, { config })
                && <SidebarItem icon={KeyIcon as any} to="aws-key-management" text="Key Management" /> }

            <SidebarItem icon={OrionIcon} to="orion" text="Orion" />

            { isAdminUser
                && <SidebarItem icon={SportsScoreIcon} to="scorecards" text="Scorecards" /> }

          <SidebarSpace />
          <SidebarGroup>
            <AssumedUserMenuItem />
          </SidebarGroup>
          <SidebarDivider />
          <SidebarItem icon={InfoIcon} to="help" text="Help Center" />
          <SidebarGroup
            label="Settings"
            icon={<UserSettingsSignInAvatar />}
            to="/settings"
          >
            <SidebarSettings />
          </SidebarGroup>
        </Sidebar>
      );
    },
  },
});
